import { useEffect, useState } from "react"
import { Flex, Modal, Dropdown, Button, Divider, Tag, Tooltip } from "antd"
import { MoreOutlined, BranchesOutlined, CopyOutlined, DeleteOutlined, UpOutlined, DownOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { comparatorData } from "../../../utils/comparatorData"
import { getAllWidgets, isMultiSelectComparator, parseConditionValueList } from "../../../utils/formsHelper"
import { useMasterOptionLabel } from "../../../hooks/useMasterOptionLabel"

const ConditionRow = ({ selectedForms=[], handleConditionClick, condition, handleConditionDelete, showDelete = true, handleConditionCopy, type }) => {
    const { t } = useTranslation();
    const { getValueLabel } = useMasterOptionLabel();
    const [expandConditions, setExpandConditions] = useState(false)
    const [allConditions, setAllConditions] = useState([])
    const [formFields, setFormFields] = useState([])

    useEffect(() => {
        if (condition && condition.scenarios) {
            let reqConditions = []
            condition.scenarios.forEach(scenario => {
                scenario.conditions.forEach(condition => {
                    reqConditions.push({...condition, action: scenario.action, approvers: scenario.approvers} )
                })
            })
            setAllConditions(reqConditions)
        }
    }, [condition])

    useEffect(() => {
        if (selectedForms?.length > 0) {
            let tempFields = [];
            selectedForms.forEach(form => {
                let currentFields = getAllWidgets(form.items);
                currentFields = currentFields.map(field => {
                    return { ...field, label: field.label, value: field.id }
                })
                tempFields = tempFields.concat(currentFields)
            })
            setFormFields(tempFields)
        }
    }, [selectedForms])

    const confirmConditionDelete = () => {
        Modal.confirm({
            title: t('Confirm'),
            content: t('Are you sure you want to delete the condition?'),
            okText: t("Yes"),
            cancelText: t("No"),
            onOk: (event) => {
                handleConditionDelete(condition.conditionId)
                Modal.destroyAll()
            },
            footer: (_, { OkBtn, CancelBtn }) => (
                <>
                    <CancelBtn />
                    <OkBtn />
                </>
            ),
        });
    }

    const confirmConditionCopy = () => {
        Modal.confirm({
            title: t('Confirm'),
            content: t('Are you sure you want to copy the condition?'),
            okText: t("Yes"),
            cancelText: t("No"),
            onOk: (event) => {
                handleConditionCopy(condition)
                Modal.destroyAll()
            },
            footer: (_, { OkBtn, CancelBtn }) => (
                <>
                    <CancelBtn />
                    <OkBtn />
                </>
            ),
        });
    }

    const conditionActions = [
        {
            key: "copy",
            label: <Flex gap={8} style={{ width: "120px" }}><CopyOutlined /><span>{t("Copy")}</span></Flex>,
            onClick: confirmConditionCopy
        },
        {
            key: "delete",
            label: <Flex gap={8} style={{ width: "120px" }}><DeleteOutlined /><span>{t("Delete")}</span></Flex>,
            danger: true,
            onClick: confirmConditionDelete
        },
    ]

    const getComparators = (reference, comparator) => {
        let refObj = formFields.find(field => field.value === reference)
        if (refObj && refObj.formType) {
            let comparators = comparatorData[refObj.formType]
            const label = comparators.find(comp => comp.value === comparator)?.label
            return label ? t(label) : ""
        }
        return ""
    }

    const getItemValue = (reference) => {
        let refObj = formFields.find(field => field.value === reference)
        if (refObj) {
            return refObj?.label
        }
        return ""
    }

    // A master backed option stores the master row's uuid, so the summary reads its
    // label off the referenced field rather than printing the stored id
    const getConditionValue = (condition) => {
        const value = condition.value
        if (value === undefined || value === null || value === "" || typeof value === "object") {
            return value
        }
        const refObj = formFields.find(field => field.value === condition.reference)
        if (!refObj) {
            return value
        }
        // A multi option comparator stores its picks as a JSON array string
        if (isMultiSelectComparator(condition.comparator, refObj.formType)) {
            return parseConditionValueList(value).map(val => getValueLabel(refObj, val)).join(", ")
        }
        return getValueLabel(refObj, value)
    }

    const getApprover = (condition) => {
        let reqApprover = ""
        if(condition.approvers) {
            reqApprover = condition.approvers.user.join(',')
        }
        return reqApprover
    }

    return (
        <div className='canvas-condition' key={condition.conditionId} >
            <Flex justify="space-between" align="center" style={{ padding: "0.75rem 1.5rem" }}>
                <Flex className="header-row" gap={16} align='center' onClick={() => handleConditionClick(condition, type)}>
                    <div className='icon-box'>
                        <BranchesOutlined style={{ color: "rgba(10, 153, 254, 1)" }} />
                    </div>
                    <div className='text-color-primary' style={{ lineHeight: "24px" }}>{condition.name}</div>
                </Flex>
                <Flex gap={16} justify="space-between">
                    {
                        showDelete ?
                            <Dropdown trigger={"click"} menu={{ items: conditionActions }} placement="bottomRight">
                                <Button icon={<MoreOutlined />} size="small"></Button>
                            </Dropdown>
                            : null
                    }
                    {
                        expandConditions ? <UpOutlined className='text-color-primary' onClick={() => setExpandConditions(false)} /> : <DownOutlined className='text-color-primary' onClick={() => setExpandConditions(true)} />
                    }

                </Flex>

            </Flex>
            {
                expandConditions ? <>
                    <Divider className="margin-0" />
                    <div className="condition-body" style={{ padding: "1rem 2.25rem" }}>
                        {
                            allConditions.map(condition => {

                                let approver = getApprover(condition)
                                return (
                                    <Flex gap={16}>
                                        <div className="icon-box">
                                            <BranchesOutlined />
                                        </div>
                                        <Flex gap={5} style={{flexWrap:"wrap"}}>
                                            <div className="text-color-secondary">{getItemValue(condition.reference)} {t('if this condition')} </div>
                                            <div className="text-color-secondary">{getComparators(condition.reference, condition.comparator)}</div>
                                            <div className="text-color-secondary">{getConditionValue(condition)}, </div>
                                            <div className="text-color-secondary">{condition.action} is</div>
                                            <Tooltip title={approver}>
                                                <Tag color={"blue"} bordered={false} >{approver}</Tag>

                                                {/* <div className="text-regular condition-approver">{approver}</div> */}

                                            </Tooltip>
                                        </Flex>

                                    </Flex>
                                )
                            })
                        }

                    </div>

                </>
                    : null
            }
        </div>
    )
}

export default ConditionRow